import { useState, useEffect, useCallback } from "react";
import { ResultsApi, Configuration } from "manager-common";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";

const MANAGER_API_BASE_URL = import.meta.env.VITE_MANAGER_API_BASE_URL;

/**
 * Hook for fetching results data and comps
 * Fetches result groups owned by the current user and list of comps
 * @param {string} compYear - Competition year (required if compid is provided)
 * @param {string} compid - USEF Comp ID (required if compYear is provided)
 */
export function useResults(compYear = null, compid = null) {
  const [results, setResults] = useState([]);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = useAuth();
  const { t } = useTranslation("common");

  const fetchData = useCallback(async () => {
    if (!auth.user?.access_token) {
      setError(t("shows.list.authError"));
      setLoading(false);
      return;
    }

    try {
      const api = new ResultsApi(
        new Configuration({
          basePath: MANAGER_API_BASE_URL,
          accessToken: auth.user.access_token,
        })
      );

      // Fetch both results and comps in parallel
      // Only fetch results if both compYear and compid are provided
      const resultsPromise = compYear && compid
        ? api.listResultGroups(compYear, compid)
        : Promise.resolve({ data: [] });

      const [resultsResponse, compsResponse] = await Promise.all([
        resultsPromise,
        api.getComps(),
      ]);

      setResults(resultsResponse.data || []);
      setComps(compsResponse.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching results/comps:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.message ||
        t("shows.list.error");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [auth.user, compYear, compid, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { results, comps, loading, error, refetch: fetchData };
}
